/**
 * LivePayment
 *
 * The two ways a live lessons charge is paid, shared by membership checkout
 * and the in-call offer sheet: Apple Pay (the browser's Payment Request
 * button) first where the device offers it, and the card below it, a saved
 * card when there is one. Both sit inside a Stripe <Elements>.
 *
 * `theme` is the surface: `light` on the app's grouped pages, `dark` on the
 * call canvas (LC-1), where text is white and hairlines are white at 0.1.
 */

import { useEffect, useRef, useState } from 'react';
import { CardElement, PaymentRequestButtonElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { PaymentRequest, PaymentRequestPaymentMethodEvent, CanMakePaymentResult } from '@stripe/stripe-js';
import { formatStripeDeclineError, listPaymentMethodsFn, type SavedCard } from '../services/stripe';

export type PaymentTheme = 'light' | 'dark';

/** How a charge was paid, for a receipt. */
export type PaidWith = { kind: 'apple_pay' } | { kind: 'saved'; card: SavedCard } | { kind: 'card' };

export interface CardSectionHandle {
  /** The saved card the server charges, or undefined for a new card. */
  paymentMethodId: string | undefined;
  paidWith: () => PaidWith;
  confirmCard: (clientSecret: string) => Promise<void>;
}

// ── Apple Pay ─────────────────────────────────────────────────────────────────

/**
 * Apple Pay (or the browser's wallet), where the device offers it. Renders
 * nothing otherwise, so the card is the fallback with no message.
 * `onAvailable` reports whether it showed, for a caller that offers one
 * button only.
 */
export function WalletButton({
  label, priceCents, disabled, theme, onPaymentMethod, onAvailable, className = 'mb-6', buttonType = 'buy', hideLink = false,
}: {
  label: string;
  /** Hide the button when Link is the only wallet: beside a saved card it is a second way to pay the same card. */
  hideLink?: boolean;
  /** Stripe's Apple Pay label; 'default' is the plain mark, with no verb. */
  buttonType?: 'buy' | 'subscribe' | 'default';
  priceCents: number;
  disabled: boolean;
  theme: PaymentTheme;
  onPaymentMethod: (ev: PaymentRequestPaymentMethodEvent) => void;
  onAvailable?: (available: boolean) => void;
  className?: string;
}) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [can, setCan] = useState<CanMakePaymentResult | null | undefined>(undefined);
  const handlerRef = useRef(onPaymentMethod);
  const availableRef = useRef(onAvailable);
  useEffect(() => { handlerRef.current = onPaymentMethod; }, [onPaymentMethod]);
  useEffect(() => { availableRef.current = onAvailable; }, [onAvailable]);

  useEffect(() => {
    if (!stripe) return;
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label, amount: priceCents },
    });
    pr.on('paymentmethod', (ev) => handlerRef.current(ev));
    let live = true;
    pr.canMakePayment().then((c) => {
      if (!live) return;
      setCan(c);
      setPaymentRequest(c ? pr : null);
    });
    return () => { live = false; };
  }, [stripe, label, priceCents]);
  const linkOnly = !!can && !!can.link && !can.applePay && !can.googlePay;
  const shown = !!paymentRequest && !(hideLink && linkOnly);
  useEffect(() => { if (can !== undefined) availableRef.current?.(shown); }, [can, shown]);

  if (!shown) return null;
  return (
    <div className={`${className} ${disabled ? 'pointer-events-none opacity-40' : ''}`}>
      <PaymentRequestButtonElement
        options={{
          paymentRequest,
          // Black on the light page; white on the call canvas, so it reads.
          style: { paymentRequestButton: { type: buttonType, theme: theme === 'dark' ? 'light' : 'dark', height: '48px' } },
        }}
      />
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────

export function CardSection({
  disabled, handleRef, theme, savedOnly = false, onSavedCard,
}: {
  disabled?: boolean;
  handleRef: React.RefObject<CardSectionHandle | null>;
  theme: PaymentTheme;
  /**
   * Whether a saved card showed, once the list has answered (false when it
   * fails): a caller hides Link beside a saved card, and keeps its pay button
   * off until this is known, so the first tap pays the card it shows.
   */
  onSavedCard?: (has: boolean) => void;
  /** Show the saved card's row only, and nothing when there is none: beside Apple Pay. */
  savedOnly?: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState('');
  const [savedCard, setSavedCard] = useState<SavedCard | undefined>(undefined);
  const savedRef = useRef(onSavedCard);
  useEffect(() => { savedRef.current = onSavedCard; }, [onSavedCard]);
  const [useSavedCard, setUseSavedCard] = useState(true);
  // The saved card list asked for and answered: until then, a row in grey,
  // never the card field a saved card then replaces.
  const [known, setKnown] = useState(false);
  const dark = theme === 'dark';

  useEffect(() => {
    listPaymentMethodsFn().then(({ data }) => {
      const def = data.paymentMethods.find((c) => c.isDefault) ?? data.paymentMethods[0];
      if (def) setSavedCard(def);
      setKnown(true);
      savedRef.current?.(!!def);
    }).catch(() => { setKnown(true); savedRef.current?.(false); });
  }, []);

  const usingSavedCard = !!savedCard && useSavedCard;

  // The handle is read only from event handlers, after render.
  useEffect(() => {
    handleRef.current = {
      paymentMethodId: usingSavedCard ? savedCard.id : undefined,
      paidWith: () => (usingSavedCard ? { kind: 'saved', card: savedCard } : { kind: 'card' }),
      async confirmCard(clientSecret) {
        if (!stripe || !elements) throw new Error('Stripe not ready');
        let result;
        if (usingSavedCard) {
          // The server already charged the saved card; this runs only when
          // the bank asks for extra authentication.
          result = await stripe.confirmCardPayment(clientSecret);
        } else {
          const cardEl = elements.getElement(CardElement);
          if (!cardEl) throw new Error('Enter your card details to continue.');
          result = await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: cardEl },
          });
        }
        if (result.error) {
          throw new Error(formatStripeDeclineError(result.error.decline_code, result.error.message));
        }
      },
    };
  });

  const text = 'text-[17px] leading-[22px] tracking-[-0.43px]';
  const primary = dark ? 'text-white' : 'text-black';
  const secondary = dark ? 'text-white/60' : 'text-[rgba(60,60,67,0.6)]';
  const action = dark ? 'text-white' : 'text-biscayne';

  if (savedOnly && !savedCard) return null;
  if (!known) {
    return (
      <div className="pl-4" data-card-loading aria-busy="true">
        <div className="flex min-h-[52px] items-center py-3 pr-4">
          <span className={`h-[17px] w-32 animate-pulse rounded-[4px] ${dark ? 'bg-white/15' : 'bg-[rgba(120,120,128,0.16)]'}`} />
        </div>
      </div>
    );
  }
  return usingSavedCard ? (
    <div className="pl-4">
      <div className="flex min-h-[52px] items-center justify-between py-3 pr-4">
        <div className="flex items-center gap-2.5">
          <span className={`${text} ${primary} capitalize`}>{savedCard.brand}</span>
          <span className={`${text} ${secondary}`}>•••• {savedCard.last4}</span>
        </div>
        <button
          type="button"
          onClick={() => setUseSavedCard(false)}
          className={`ios-press relative flex items-center gap-1 text-[15px] leading-[20px] ${action} after:absolute after:-inset-x-2 after:-inset-y-3 after:content-['']`}
        >
          Use Different Card
        </button>
      </div>
    </div>
  ) : (
    <div className="pl-4">
      {/* A 52 row, as every field in a group (8.1); Stripe's iframe is centred in it. */}
      <div className={`flex min-h-[52px] items-center py-3 pr-4 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="w-full">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '17px',
                color: dark ? '#FFFFFF' : '#1C1C1C',
                iconColor: dark ? 'rgba(255,255,255,0.6)' : undefined,
                fontFamily: 'Inter, system-ui, sans-serif',
                '::placeholder': { color: dark ? 'rgba(255,255,255,0.4)' : 'rgba(60,60,67,0.3)' },
              },
              invalid: { color: dark ? '#FF8A80' : '#DC2626' },
            },
            hidePostalCode: true,
          }}
          onChange={(e) => setCardError(e.error?.message ?? '')}
        />
        </div>
      </div>
      {cardError && (
        <div className={`pb-3 pr-4 text-[13px] leading-[18px] ${dark ? 'text-white/85' : 'text-danger'}`}>{cardError}</div>
      )}
    </div>
  );
}
